import { formatDateTime } from "@/common/dateUtils";
import Chat from "@/components/chat/Chat.tsx";
import DeepInsightPDF from "@/components/deep-insight/DeepInsightPDFGenerator.tsx";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { getDeepInsightDetails } from "@/lib/serverComm.tsx";
import {
  DEEP_INSIGHT_CHAT_CONTEXT_PREFIX,
  DEEP_INSIGHT_CHAT_CONTEXT_SUFFIX,
  DeepInsightWithContent,
} from "@/types/deep-insights.ts";
import { PDFDownloadLink } from "@react-pdf/renderer";
import ReactECharts from "echarts-for-react";
import { ArrowLeft } from "lucide-react";
import React, { ReactNode, useEffect, useState } from "react";
import Markdown from "react-markdown";
import { Link, useParams } from "react-router-dom";

const DeepInsightDetails: React.FC = () => {
  const { insightId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [insight, setInsight] = useState<DeepInsightWithContent | null>(null);
  const [insightError, setInsightError] = useState(false);
  const [embeddedContext, setEmbeddedContext] = useState("");
  const [chartImage, setChartImage] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading || insight !== null || insightError) {
      return;
    }

    setIsLoading(true);
    setInsightError(false);

    const fetchData = async () => {
      // TODO Handle the error here
      if (insightId === undefined) {
        return;
      }

      const response = await getDeepInsightDetails(insightId);

      if (response !== null) {
        response.content.chart.grid = {
          // set defaults
          left: "3%",
          right: "4%",
          bottom: "5%",
          containLabel: true,
          // add custom if they exist
          ...response.content.chart.grid,
          // add adjustment
          top: "20%",
        };

        response.content.chart.legend = {
          // add custom if they exist
          ...response.content.chart.legend,
          // add adjustment
          top: "10%",
        };
      }

      setIsLoading(false);
      setInsight(response);
      setInsightError(response === null);

      setEmbeddedContext(
        response !== null
          ? DEEP_INSIGHT_CHAT_CONTEXT_PREFIX +
              JSON.stringify(response.content) +
              DEEP_INSIGHT_CHAT_CONTEXT_SUFFIX
          : "",
      );
    };

    fetchData().then();
  }, [isLoading, insight, insightError, insightId]);

  useEffect(() => {
    if (insight?.content.chart) {
      // small timeout ensures chart is rendered in DOM
      setTimeout(() => {
        const chartInstance = document.querySelector("canvas");
        if (chartInstance instanceof HTMLCanvasElement) {
          setChartImage(chartInstance.toDataURL("image/png"));
          setIsChartLoading(false);
        }
      }, 1500); // we need to wait for the data and the animation
    }
  }, [insight]);

  function stateSwitch(
    loader: ReactNode,
    error: ReactNode,
    success: ReactNode,
  ) {
    if (isLoading) {
      return loader;
    }

    if (insightError) {
      return error;
    }

    return success;
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-8">
        <div className="flex items-center w-full gap-4">
          <Link to={`/deep-insights`}>
            <ArrowLeft
              size={24}
              className="rounded-full text-sky-500 bg-white p-1"
            />
          </Link>
          <h1 className="text-sky-500 text-2xl font-bold">Deep Insights</h1>
        </div>

        {stateSwitch(
          <p className="mt-8">Loading...</p>,
          <p className="mt-8">Error while loading a Deep Insight.</p>,
          insight !== null && (
            <>
              <p className="text-xs">
                Created:{" "}
                <strong>
                  {formatDateTime(new Date(insight.content.updateDateTime))}
                </strong>
              </p>

              <div className="m-11">
                <div className="border rounded-lg border-gray-300 bg-white p-5 flex flex-col flex-1">
                  <div className="bg-sky-500 text-white font-bold pt-2 pb-2 pr-5 pl-5 rounded-full">
                    Deep Insights
                  </div>
                  <div className="mt-5 mb-10">
                    Category: <strong>{insight.category}</strong>
                  </div>

                  <h1 className="font-bold text-black text-2xl pb-6">
                    {insight.content.title}
                  </h1>

                  {insight.content.sections.map((section) => (
                    <div key={section.title}>
                      <h2 className="font-bold text-black text-l pb-3">
                        {section.title}
                      </h2>
                      <div className="text-black text-l pb-6">
                        <Markdown>{section.content}</Markdown>
                      </div>
                    </div>
                  ))}

                  {insight.content.chart !== undefined && (
                    <div>
                      <ReactECharts
                        option={insight.content.chart}
                        style={{ height: "500px", width: "100%" }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {insight && (
                <div className="mb-6">
                  <PDFDownloadLink
                    document={
                      <DeepInsightPDF
                        report={insight.content}
                        chartImage={chartImage}
                      />
                    }
                    fileName={`${insight.content.title}.pdf`}
                  >
                    {isChartLoading ? (
                      <button
                        className="px-4 py-2 bg-gray-300 rounded"
                        disabled={isChartLoading}
                      >
                        Preparing PDF...
                      </button>
                    ) : (
                      <button className="px-4 py-2 bg-sky-500 text-white rounded hover:bg-sky-600">
                        Download PDF
                      </button>
                    )}
                  </PDFDownloadLink>
                </div>
              )}

              <div>
                <Chat
                  embedded={true}
                  embeddedContext={embeddedContext}
                  insightId={insightId}
                />
              </div>
            </>
          ),
        )}
      </div>
    </ScrollArea>
  );
};

export default DeepInsightDetails;
